import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Globe, Check } from 'lucide-react';

// 语言配置
const languages = [
  { code: 'en', name: 'English', flag: '🇬🇧', greeting: 'Welcome to ArtCanvas' },
  { code: 'zh', name: '中文', flag: '🇨🇳', greeting: '欢迎来到 ArtCanvas' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺', greeting: 'Добро пожаловать в ArtCanvas' },
  { code: 'uk', name: 'Українська', flag: '🇺🇦', greeting: 'Ласкаво просимо до ArtCanvas' },
  { code: 'fr', name: 'Français', flag: '🇫🇷', greeting: 'Bienvenue sur ArtCanvas' },
  { code: 'es', name: 'Español', flag: '🇪🇸', greeting: 'Bienvenido a ArtCanvas' },
  { code: 'pt', name: 'Português', flag: '🇵🇹', greeting: 'Bem-vindo ao ArtCanvas' },
  { code: 'ja', name: '日本語', flag: '🇯🇵', greeting: 'ArtCanvasへようこそ' },
  { code: 'ko', name: '한국어', flag: '🇰🇷', greeting: 'ArtCanvas에 오신 것을 환영합니다' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦', greeting: 'مرحباً بك في ArtCanvas' },
  { code: 'uz', name: "O'zbekcha", flag: '🇺🇿', greeting: "ArtCanvas'ga xush kelibsiz" }
];

// 本地存储键名
const LANGUAGE_CONFIRMED_KEY = 'artcanvas_language_confirmed';

/**
 * 首次访问语言确认弹窗
 * 当用户首次访问网站时，显示语言选择弹窗让用户确认或更改语言
 */
export const LanguageConfirmDialog = () => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [selectedLang, setSelectedLang] = useState(i18n.language?.split('-')[0] || 'en');
  const [hasInitialized, setHasInitialized] = useState(false);

  useEffect(() => {
    // 防止 React Strict Mode 下重复执行
    if (hasInitialized) return;

    // 检查用户是否已经确认过语言
    try {
      const confirmed = localStorage.getItem(LANGUAGE_CONFIRMED_KEY);
      
      if (confirmed === 'true') {
        // 已确认过，不显示弹窗
        setHasInitialized(true);
        return;
      }
      
      // 首次访问，延迟显示弹窗以让页面先加载
      const timer = setTimeout(() => {
        const currentLang = i18n.language?.split('-')[0] || 'en';
        setSelectedLang(currentLang);
        setOpen(true);
        setHasInitialized(true);
      }, 800);
      
      return () => clearTimeout(timer);
    } catch (e) {
      // localStorage 不可用，不显示弹窗
      console.warn('localStorage not available:', e);
      setHasInitialized(true);
    }
  }, [hasInitialized, i18n.language]); // 依赖 hasInitialized 状态

  const handleConfirm = () => {
    // 切换到选择的语言
    i18n.changeLanguage(selectedLang);
    // 标记语言已确认
    try {
      localStorage.setItem(LANGUAGE_CONFIRMED_KEY, 'true');
    } catch (e) {
      console.warn('Failed to save language preference:', e);
    }
    setOpen(false);
  };

  // 关闭弹窗时也标记为已确认（用户点击外部关闭）
  const handleOpenChange = (isOpen) => {
    if (!isOpen) {
      // 用户关闭弹窗，也视为已确认
      try {
        localStorage.setItem(LANGUAGE_CONFIRMED_KEY, 'true');
      } catch (e) {
        console.warn('Failed to save language preference:', e);
      }
    }
    setOpen(isOpen);
  };

  const handleLanguageSelect = (langCode) => {
    setSelectedLang(langCode);
  };

  // 获取当前选中语言的问候语
  const currentGreeting = languages.find(l => l.code === selectedLang)?.greeting || '';

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent 
        className="sm:max-w-md max-h-[90vh] flex flex-col"
        data-testid="language-confirm-dialog"
      >
        <DialogHeader className="text-center flex-shrink-0">
          <div className="mx-auto w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mb-4">
            <Globe className="w-6 h-6 text-amber-600" />
          </div>
          <DialogTitle className="text-xl font-semibold text-center text-amber-900">
            {currentGreeting}
          </DialogTitle>
          <DialogDescription className="text-center text-amber-600 mt-2">
            {selectedLang === 'zh' && '请选择您偏好的语言'}
            {selectedLang === 'en' && 'Please select your preferred language'}
            {selectedLang === 'ru' && 'Пожалуйста, выберите язык'}
            {selectedLang === 'uk' && 'Будь ласка, оберіть мову'}
            {selectedLang === 'fr' && 'Veuillez sélectionner votre langue'}
            {selectedLang === 'es' && 'Por favor seleccione su idioma'}
            {selectedLang === 'pt' && 'Por favor selecione seu idioma'}
            {selectedLang === 'ja' && 'ご希望の言語を選択してください'}
            {selectedLang === 'ko' && '원하시는 언어를 선택해주세요'}
            {selectedLang === 'ar' && 'يرجى اختيار لغتك المفضلة'}
            {selectedLang === 'uz' && 'Iltimos, tilni tanlang'}
          </DialogDescription>
        </DialogHeader>

        {/* 可滚动的语言列表区域 */}
        <div className="flex-1 overflow-y-auto min-h-0 py-4">
          <div className="grid gap-3 pr-2">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageSelect(lang.code)}
                data-testid={`lang-option-${lang.code}`}
                className={`
                  flex items-center justify-between p-4 rounded-lg border-2 transition-all
                  ${selectedLang === lang.code 
                    ? 'border-green-500 bg-green-50' 
                    : 'border-amber-200 hover:border-green-300 hover:bg-amber-50'
                  }
                `}
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{lang.flag}</span>
                  <span className="font-medium">{lang.name}</span>
                </div>
                {selectedLang === lang.code && (
                  <Check className="w-5 h-5 text-green-600" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* 固定在底部的确认按钮 */}
        <div className="flex-shrink-0 pt-2 border-t border-amber-100">
          <Button 
            onClick={handleConfirm}
            className="w-full bg-green-600 hover:bg-green-700"
            data-testid="confirm-language-btn"
          >
            {selectedLang === 'zh' && '确认'}
            {selectedLang === 'en' && 'Confirm'}
            {selectedLang === 'ru' && 'Подтвердить'}
            {selectedLang === 'uk' && 'Підтвердити'}
            {selectedLang === 'fr' && 'Confirmer'}
            {selectedLang === 'es' && 'Confirmar'}
            {selectedLang === 'pt' && 'Confirmar'}
            {selectedLang === 'ja' && '確認'}
            {selectedLang === 'ko' && '확인'}
            {selectedLang === 'ar' && 'تأكيد'}
            {selectedLang === 'uz' && 'Tasdiqlash'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LanguageConfirmDialog;
